import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePlant } from '../hooks/usePlant';
import { diagnoseProblem } from '../services/api';
import { formatLastWatered } from '../services/plants';
import { DiagnosisUpload } from '../components/diagnosis/DiagnosisUpload';
import { DiagnosisResult } from '../components/diagnosis/DiagnosisResult';
import { BottomSheet } from '../components/common/BottomSheet';
import { PlantChat } from '../components/plants/PlantChat';
import type { DiagnoseResponse } from '../types/diagnosis';

type DiagnosisState = 'upload' | 'analyzing' | 'result' | 'error';

/** Extract minutes from rate limit error message */
function extractRetryMinutes(errorMessage: string): number | null {
  const match = errorMessage.match(/in (\d+) minutes/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}

export default function DiagnosePlant() {
  const { id: plantId } = useParams<{ id: string }>();
  const { user } = useAuth();

  const { plant, loading: plantLoading, error: plantError } = usePlant(user?.uid ?? null, plantId ?? null);

  const [state, setState] = useState<DiagnosisState>('upload');
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [result, setResult] = useState<DiagnoseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number>(0);
  const [chatOpen, setChatOpen] = useState(false);
  const countdownRef = useRef<number | null>(null);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (rateLimitSeconds <= 0) {
      if (countdownRef.current) {
        window.clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    countdownRef.current = window.setInterval(() => {
      setRateLimitSeconds((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            window.clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          setError(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        window.clearInterval(countdownRef.current);
      }
    };
  }, [rateLimitSeconds > 0]);

  const handleImageReady = useCallback((base64: string) => {
    setImageBase64(base64);
    setError(null);
  }, []);

  const handleImageError = useCallback((message: string) => {
    setError(message);
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!imageBase64 || !plant || !user) return;

    if (!plant.scientificName) {
      setError('Only identified plants can be diagnosed. Custom plants are not supported.');
      return;
    }

    setState('analyzing');
    setAnalyzing(true);
    setError(null);

    try {
      const response = await diagnoseProblem({
        plantId: plant.id,
        imageBase64,
        plantContext: {
          scientificName: plant.scientificName,
          commonName: plant.commonName,
          lastWatered: formatLastWatered(plant.lastWatered),
          location: plant.location,
        },
      });

      setResult(response);
      setState('result');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze plant. Please try again.';
      setError(errorMessage);

      const retryMinutes = extractRetryMinutes(errorMessage);
      if (retryMinutes !== null) {
        setRateLimitSeconds(retryMinutes * 60);
        setState('upload');
      } else {
        setState('error');
      }
    } finally {
      setAnalyzing(false);
    }
  }, [imageBase64, plant, user]);

  const handleRetry = useCallback(() => {
    setState('upload');
    setResult(null);
    setError(null);
  }, []);

  const handleNewDiagnosis = useCallback(() => {
    setState('upload');
    setImageBase64(null);
    setResult(null);
    setError(null);
  }, []);

  // Loading state
  if (plantLoading) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
          <span className="material-symbols-outlined text-primary animate-pulse" style={{ fontSize: 32 }} aria-hidden="true">
            monitor_heart
          </span>
          <span className="text-gray-500 font-serif text-sm">Loading...</span>
        </div>
      </main>
    );
  }

  // Plant not found
  if (plantError || !plant) {
    return (
      <main className="min-h-screen bg-background p-6">
        <div className="max-w-md mx-auto text-center py-12">
          <h1 className="font-display text-2xl font-bold text-editorial-black mb-2">Plant not found</h1>
          <p className="font-serif text-gray-500 mb-6">
            This plant may have been deleted or you don&rsquo;t have access to it.
          </p>
          <Link to="/home" className="btn-editorial inline-flex">
            <span>Go to My Plants</span>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
              arrow_forward
            </span>
          </Link>
        </div>
      </main>
    );
  }

  // Custom plant restriction
  if (!plant.scientificName) {
    return (
      <main className="min-h-screen bg-background p-6">
        <div className="max-w-md mx-auto">
          <div className="border border-editorial-gold/30 bg-editorial-gold/5 rounded-lg p-5 text-center">
            <span className="material-symbols-outlined text-editorial-gold mb-3 block" style={{ fontSize: 32 }} aria-hidden="true">
              info
            </span>
            <h2 className="font-display font-bold text-editorial-black mb-2">Diagnosis not available</h2>
            <p className="text-sm font-serif text-gray-500">
              Plant diagnosis requires species data. Custom plants cannot be diagnosed.
            </p>
          </div>
          <div className="mt-6 text-center">
            <Link
              to={`/plant/${plant.id}`}
              className="text-sm font-sans text-gray-400 hover:text-editorial-black transition-colors"
            >
              Back to {plant.nickname || plant.commonName}
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background pb-8">
      <div className="max-w-md mx-auto px-6 pt-4">
        {/* Upload state */}
        {state === 'upload' && (
          <div className="space-y-6">
            <div>
              <h2 className="font-display text-xl font-bold text-editorial-black mb-1">
                Take a photo of the problem
              </h2>
              <p className="text-sm font-serif text-gray-500">
                Capture leaves, stems, or soil showing symptoms for the best diagnosis.
              </p>
            </div>

            <DiagnosisUpload
              onImageReady={handleImageReady}
              onError={handleImageError}
              disabled={analyzing}
            />

            {error && (
              <div className="p-4 border border-danger/30 bg-danger/5 rounded-lg" role="alert">
                <p className="text-sm font-sans text-danger">{error}</p>
                {rateLimitSeconds > 0 && (
                  <p className="text-sm font-sans font-bold text-danger mt-2">
                    Try again in {Math.floor(rateLimitSeconds / 60)}:{(rateLimitSeconds % 60).toString().padStart(2, '0')}
                  </p>
                )}
              </div>
            )}

            {imageBase64 && (
              <button
                onClick={handleAnalyze}
                disabled={analyzing || rateLimitSeconds > 0}
                className="btn-editorial group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{analyzing ? 'Analyzing...' : rateLimitSeconds > 0 ? 'Rate Limited' : 'Analyze Photo'}</span>
                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform" style={{ fontSize: 20 }} aria-hidden="true">
                  arrow_forward
                </span>
              </button>
            )}

            <div className="text-center">
              <Link
                to={`/plant/${plant.id}`}
                className="text-sm font-sans text-gray-400 hover:text-editorial-black transition-colors"
              >
                Cancel
              </Link>
            </div>
          </div>
        )}

        {/* Analyzing state */}
        {state === 'analyzing' && (
          <div className="text-center py-16">
            <span
              className="material-symbols-outlined text-primary animate-pulse mb-4 block"
              style={{ fontSize: 40 }}
              aria-hidden="true"
            >
              biotech
            </span>
            <h2 className="text-xl font-display font-bold text-editorial-black mb-2">
              Analyzing your plant
            </h2>
            <p className="font-serif text-gray-500">
              Our plant expert is examining the photo...
            </p>
            <p className="text-xs font-sans text-gray-400 mt-4 uppercase tracking-wider">
              Usually takes 10-30 seconds
            </p>
          </div>
        )}

        {/* Error state */}
        {state === 'error' && (
          <div className="space-y-6">
            <div className="border border-danger/30 bg-danger/5 rounded-lg p-5" role="alert">
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-danger mt-0.5" style={{ fontSize: 20 }} aria-hidden="true">
                  error
                </span>
                <div>
                  <h2 className="font-display font-bold text-editorial-black mb-1">Analysis failed</h2>
                  <p className="text-sm font-serif text-gray-500">{error}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleRetry}
                className="flex-1 btn-editorial"
              >
                <span>Try Again</span>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
                  refresh
                </span>
              </button>
              <button
                onClick={handleNewDiagnosis}
                className="flex-1 h-12 flex items-center justify-center gap-2 border border-gray-300 rounded-lg font-sans font-bold text-xs uppercase tracking-widest text-editorial-black hover:border-gray-500 transition-colors"
              >
                New Photo
              </button>
            </div>
          </div>
        )}

        {/* Result state */}
        {state === 'result' && result && (
          <div className="space-y-6">
            <DiagnosisResult
              isHealthy={result.diagnosis.isHealthy}
              problems={result.diagnosis.problems}
              products={result.diagnosis.products}
              generalTips={result.diagnosis.generalTips}
              photoURL={result.diagnosis.photoURL}
            />

            {/* Ask the specialist */}
            <button
              onClick={() => setChatOpen(true)}
              className="btn-editorial w-full group"
              aria-label={`Ask the ${plant.commonName} specialist about this diagnosis`}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
                forum
              </span>
              <span>Ask the specialist</span>
            </button>

            {/* Bottom actions */}
            <div className="flex gap-3">
              <button
                onClick={handleNewDiagnosis}
                className="flex-1 h-12 flex items-center justify-center gap-2 border border-gray-300 rounded-lg font-sans font-bold text-xs uppercase tracking-widest text-editorial-black hover:border-gray-500 transition-colors"
              >
                New Diagnosis
              </button>
              <Link
                to={`/plant/${plant.id}`}
                className="flex-1 btn-editorial"
              >
                <span>View Care Guide</span>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
                  arrow_forward
                </span>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Diagnosis specialist chat */}
      {state === 'result' && result && (
        <BottomSheet
          open={chatOpen}
          onOpenChange={setChatOpen}
          title={`${plant.commonName} specialist`}
          initialSnap="full"
        >
          <PlantChat
            plant={plant}
            diagnosisContext={{
              isHealthy: result.diagnosis.isHealthy,
              problems: result.diagnosis.problems,
              products: result.diagnosis.products,
              generalTips: result.diagnosis.generalTips,
            }}
            persist={{ plantId: plant.id, diagnosisId: result.diagnosis.id }}
            initialMessages={[]}
          />
        </BottomSheet>
      )}
    </main>
  );
}
